import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Dropdown, Input } from '../../../../common/ui';
import Axios from '../../../../common/util/Axios';
import { setBranchId } from '../../../auth/actions';
import { setFiltersVisibility } from '../../../../common/actions';
import AgreementTable from './AgreementTable';
import FilterComp from './FilterComp';
import styles from './styles.module.css';

const EMPTY_DATE_RANGE = { startDate: null, endDate: null };

const STATUS_LABELS = {
  active: 'Active',
  overdue: 'Overdue',
  settled: 'Settled',
  defaulted: 'Defaulted',
  repossessed: 'Repossessed',
  settled_early: 'Settled Early',
};

const hasDateRange = (range) => Boolean(range?.startDate && range?.endDate);

const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toApiDate = (date) => {
  const localDate = toLocalDate(date);
  if (!localDate) return null;
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTag = (from, to) => {
  const format = (value) => {
    const localDate = toLocalDate(value);
    if (!localDate) return '';
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  return `${format(from)} - ${format(to)}`;
};

const parseAmount = (value) => {
  if (value === '' || value == null) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

const formatAmountTag = (from, to) => {
  const min = parseAmount(from);
  const max = parseAmount(to);
  if (min != null && max != null) {
    return `${min.toFixed(2)} - ${max.toFixed(2)}`;
  }
  if (min != null) {
    return `from ${min.toFixed(2)}`;
  }
  if (max != null) {
    return `up to ${max.toFixed(2)}`;
  }
  return '';
};

const hasOverdueAmountRange = (min, max) =>
  parseAmount(min) != null || parseAmount(max) != null;

const buildFilterTags = (filters, hpPlanOptions) => {
  const tags = [];

  if (filters.status) {
    tags.push({
      key: 'status',
      label: `Status: ${STATUS_LABELS[filters.status] || filters.status}`,
    });
  }
  if (filters.startDateFrom && filters.startDateTo) {
    tags.push({
      key: 'startDate',
      label: `Start Date: ${formatDateTag(
        filters.startDateFrom,
        filters.startDateTo
      )}`,
    });
  }
  if (filters.closureDateFrom && filters.closureDateTo) {
    tags.push({
      key: 'closureDate',
      label: `Closure Date: ${formatDateTag(
        filters.closureDateFrom,
        filters.closureDateTo
      )}`,
    });
  }
  if (filters.dueDateFrom && filters.dueDateTo) {
    tags.push({
      key: 'dueDate',
      label: `Due Date: ${formatDateTag(filters.dueDateFrom, filters.dueDateTo)}`,
    });
  }
  if (filters.maturityDateFrom && filters.maturityDateTo) {
    tags.push({
      key: 'maturityDate',
      label: `Maturity Date: ${formatDateTag(
        filters.maturityDateFrom,
        filters.maturityDateTo
      )}`,
    });
  }
  if (filters.hpPlanId) {
    const planLabel =
      hpPlanOptions.find((plan) => plan.value === filters.hpPlanId)?.label ||
      filters.planName ||
      filters.hpPlanId;
    tags.push({
      key: 'hpPlan',
      label: `Plan: ${planLabel}`,
    });
  }
  if (filters.overdueAmountFrom != null || filters.overdueAmountTo != null) {
    const label = formatAmountTag(
      filters.overdueAmountFrom,
      filters.overdueAmountTo
    );
    if (label) {
      tags.push({
        key: 'overdueAmount',
        label: `Overdue Amount: ${label}`,
      });
    }
  }

  return tags;
};

const Agreements = ({
  auth: { companyId, branchId, branchList },
  commonState,
  setBranchId,
  setFiltersVisibility,
}) => {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAgreementDateType, setSelectedAgreementDateType] = useState('');
  const [selectedAgreementDateRange, setSelectedAgreementDateRange] =
    useState(EMPTY_DATE_RANGE);
  const [selectedDueDateRange, setSelectedDueDateRange] =
    useState(EMPTY_DATE_RANGE);
  const [selectedHpPlanId, setSelectedHpPlanId] = useState('');
  const [selectedOverdueAmountMin, setSelectedOverdueAmountMin] = useState('');
  const [selectedOverdueAmountMax, setSelectedOverdueAmountMax] = useState('');
  const [hpPlanOptions, setHpPlanOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadHpPlans = async () => {
      if (!companyId) {
        setHpPlanOptions([]);
        return;
      }

      try {
        const res = await Axios.get('/api/dashboard/hire-purchase/plans', {
          params: { companyId },
        });
        const plans = Array.isArray(res?.data?.data) ? res.data.data : [];
        setHpPlanOptions(
          plans.map((plan) => ({
            value: plan.id,
            label: plan.planName || plan.id,
          }))
        );
      } catch (error) {
        setHpPlanOptions([]);
      }
    };

    loadHpPlans();
  }, [companyId]);

  const filterTags = useMemo(
    () => buildFilterTags(filters, hpPlanOptions),
    [filters, hpPlanOptions]
  );

  const handleSetFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
    setFiltersVisibility(false);
  };

  const handleApplyFilters = () => {
    const newFilters = {};

    if (selectedStatus) {
      newFilters.status = selectedStatus;
    }
    if (selectedAgreementDateType && hasDateRange(selectedAgreementDateRange)) {
      const from = toApiDate(selectedAgreementDateRange.startDate);
      const to = toApiDate(selectedAgreementDateRange.endDate);
      if (selectedAgreementDateType === 'startDate') {
        newFilters.startDateFrom = from;
        newFilters.startDateTo = to;
      } else if (selectedAgreementDateType === 'closureDate') {
        newFilters.closureDateFrom = from;
        newFilters.closureDateTo = to;
      } else if (selectedAgreementDateType === 'maturityDate') {
        newFilters.maturityDateFrom = from;
        newFilters.maturityDateTo = to;
      }
    }
    if (hasDateRange(selectedDueDateRange)) {
      newFilters.dueDateFrom = toApiDate(selectedDueDateRange.startDate);
      newFilters.dueDateTo = toApiDate(selectedDueDateRange.endDate);
    }
    if (selectedHpPlanId) {
      newFilters.hpPlanId = selectedHpPlanId;
      const selectedPlan = hpPlanOptions.find(
        (plan) => plan.value === selectedHpPlanId
      );
      if (selectedPlan?.label) {
        newFilters.planName = selectedPlan.label;
      }
    }
    if (hasOverdueAmountRange(selectedOverdueAmountMin, selectedOverdueAmountMax)) {
      const min = parseAmount(selectedOverdueAmountMin);
      const max = parseAmount(selectedOverdueAmountMax);
      if (min != null) {
        newFilters.overdueAmountFrom = min;
      }
      if (max != null) {
        newFilters.overdueAmountTo = max;
      }
    }

    handleSetFilters(newFilters);
  };

  const clearAgreementDateFilter = () => {
    setSelectedAgreementDateType('');
    setSelectedAgreementDateRange(EMPTY_DATE_RANGE);
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const updated = { ...prev };

      if (key === 'status') {
        delete updated.status;
        setSelectedStatus('');
      } else if (key === 'startDate') {
        delete updated.startDateFrom;
        delete updated.startDateTo;
        clearAgreementDateFilter();
      } else if (key === 'closureDate') {
        delete updated.closureDateFrom;
        delete updated.closureDateTo;
        clearAgreementDateFilter();
      } else if (key === 'dueDate') {
        delete updated.dueDateFrom;
        delete updated.dueDateTo;
        setSelectedDueDateRange(EMPTY_DATE_RANGE);
      } else if (key === 'maturityDate') {
        delete updated.maturityDateFrom;
        delete updated.maturityDateTo;
        clearAgreementDateFilter();
      } else if (key === 'hpPlan') {
        delete updated.hpPlanId;
        delete updated.planName;
        setSelectedHpPlanId('');
      } else if (key === 'overdueAmount') {
        delete updated.overdueAmountFrom;
        delete updated.overdueAmountTo;
        setSelectedOverdueAmountMin('');
        setSelectedOverdueAmountMax('');
      }

      return updated;
    });
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSelectedStatus('');
    clearAgreementDateFilter();
    setSelectedDueDateRange(EMPTY_DATE_RANGE);
    setSelectedHpPlanId('');
    setSelectedOverdueAmountMin('');
    setSelectedOverdueAmountMax('');
    setPage(0);
  };

  useEffect(() => {
    const loadAgreements = async () => {
      if (!companyId || !branchId) return;
      setIsLoading(true);
      try {
        const res = await Axios.get('/api/dashboard/hire-purchase/agreements', {
          params: {
            companyId,
            branchId,
            page: page + 1,
            rowsPerPage,
            keyword: searchKeyword.trim(),
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.startDateFrom
              ? { startDateFrom: filters.startDateFrom }
              : {}),
            ...(filters.startDateTo ? { startDateTo: filters.startDateTo } : {}),
            ...(filters.closureDateFrom
              ? { closureDateFrom: filters.closureDateFrom }
              : {}),
            ...(filters.closureDateTo
              ? { closureDateTo: filters.closureDateTo }
              : {}),
            ...(filters.dueDateFrom ? { dueDateFrom: filters.dueDateFrom } : {}),
            ...(filters.dueDateTo ? { dueDateTo: filters.dueDateTo } : {}),
            ...(filters.maturityDateFrom
              ? { maturityDateFrom: filters.maturityDateFrom }
              : {}),
            ...(filters.maturityDateTo
              ? { maturityDateTo: filters.maturityDateTo }
              : {}),
            ...(filters.hpPlanId ? { hpPlanId: filters.hpPlanId } : {}),
            ...(filters.overdueAmountFrom != null
              ? { overdueAmountFrom: filters.overdueAmountFrom }
              : {}),
            ...(filters.overdueAmountTo != null
              ? { overdueAmountTo: filters.overdueAmountTo }
              : {}),
          },
        });
        setRows(Array.isArray(res?.data?.data) ? res.data.data : []);
        setTotalCount(Number(res?.data?.total) || 0);
      } catch (error) {
        setRows([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadAgreements();
  }, [companyId, branchId, page, rowsPerPage, searchKeyword, filters]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Hire Purchase Agreement</h1>
          <p className={styles.subtitle}>Hire Purchase / Agreements</p>
        </div>
        <div className={styles.branchSelectWrap}>
          <Dropdown
            label="Branch"
            id="branch-select-hp"
            options={(branchList || []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            value={branchId}
            onChange={setBranchId}
          />
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarRight}>
            <Input
              id="hp-agreement-search"
              placeholder="Search by Agreement No / Customer"
              value={searchKeyword}
              onChange={(value) => {
                setSearchKeyword(value);
                setPage(0);
              }}
              className={styles.searchInput}
            />
            <Button
              variant="secondary"
              onClick={() => setFiltersVisibility(!commonState.isFiltersOpen)}
            >
              Filters
            </Button>
            <FilterComp
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedAgreementDateType={selectedAgreementDateType}
              setSelectedAgreementDateType={setSelectedAgreementDateType}
              selectedAgreementDateRange={selectedAgreementDateRange}
              setSelectedAgreementDateRange={setSelectedAgreementDateRange}
              selectedDueDateRange={selectedDueDateRange}
              setSelectedDueDateRange={setSelectedDueDateRange}
              selectedHpPlanId={selectedHpPlanId}
              setSelectedHpPlanId={setSelectedHpPlanId}
              selectedOverdueAmountMin={selectedOverdueAmountMin}
              setSelectedOverdueAmountMin={setSelectedOverdueAmountMin}
              selectedOverdueAmountMax={selectedOverdueAmountMax}
              setSelectedOverdueAmountMax={setSelectedOverdueAmountMax}
              hpPlanOptions={hpPlanOptions}
              onApply={handleApplyFilters}
            />
          </div>
        </div>

        <div className={styles.addedFiltersDiv}>
          {filterTags.map((tag) => (
            <span key={tag.key} className={styles.filterTag}>
              {tag.label}
              <button
                type="button"
                className={styles.removeFilterBtn}
                onClick={() => handleRemoveFilter(tag.key)}
                aria-label={`Remove ${tag.key} filter`}
              >
                ×
              </button>
            </span>
          ))}
          {filterTags.length > 0 && (
            <button
              type="button"
              className={styles.resetFiltersBtn}
              onClick={handleResetFilters}
            >
              Reset filters
            </button>
          )}
        </div>

        <AgreementTable
          rows={rows}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          isLoading={isLoading}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(valueOrEvent) => {
            const next =
              typeof valueOrEvent === 'object' &&
              valueOrEvent?.target?.value != null
                ? Number(valueOrEvent.target.value)
                : Number(valueOrEvent);
            setRowsPerPage(Number.isFinite(next) && next > 0 ? next : 10);
            setPage(0);
          }}
        />
      </div>
    </div>
  );
};

Agreements.propTypes = {
  auth: PropTypes.shape({
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    branchList: PropTypes.array,
  }).isRequired,
  commonState: PropTypes.shape({
    isFiltersOpen: PropTypes.bool,
  }).isRequired,
  setBranchId: PropTypes.func.isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  commonState: state.commonState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setFiltersVisibility,
})(Agreements);
